import classes from './PatientProfileDrawer.module.css';
import { Burger, Drawer, UnstyledButton } from '@mantine/core';
import useLogOut from '@/hooks/useLogOut';
import { useDisclosure } from '@mantine/hooks';
import { useState, FunctionComponent } from 'react';
import PersonalDetails from './PersonalDetails/PersonalDetails';
import { Support } from './Support/Support';
import { NotificationSettings } from './NotificationSettings/NotificationSettings';
import { Legal } from './Legal/Legal';
import Icon from '@/components/Icon';
import {
  AuthenticationSource,
  useAuthentication,
} from '@/contexts/Authentication';
import ActionIcon from '@/components/ActionIcon';

type SubSection = {
  label: string;
  component: FunctionComponent<{
    onGoBack: () => void;
    closeDrawer: () => void;
  }>;
  requiresFullLogin: boolean;
};

const SUB_SECTIONS: Record<string, SubSection> = {
  PERSONAL_DETAILS: {
    label: 'Personal Details',
    component: PersonalDetails,
    requiresFullLogin: true,
  },
  NOTIFICATIONS: {
    label: 'Notifications',
    component: NotificationSettings,
    requiresFullLogin: true,
  },
  LEGAL: {
    label: 'Legal',
    component: Legal,
    requiresFullLogin: false,
  },
  SUPPORT: {
    label: 'Support',
    component: Support,
    requiresFullLogin: false,
  },
};

const PatientProfileDrawer = () => {
  const [opened, { toggle }] = useDisclosure(false);
  const [selectedSubSection, setSelectedSubSection] = useState<string | null>(
    null
  );
  const { source } = useAuthentication();

  const clearCacheAndLogout = useLogOut();

  const SelectedSubSection = selectedSubSection
    ? SUB_SECTIONS[selectedSubSection].component
    : undefined;

  const handleClose = () => {
    toggle();
    setSelectedSubSection(null);
  };

  const handleGoBack = () => {
    setSelectedSubSection(null);
  };

  return (
    <>
      <Drawer
        opened={opened}
        onClose={handleClose}
        position="right"
        withCloseButton={false}
        classNames={{
          content: classes.drawerContent,
          body: classes.drawerBody,
        }}
        className={classes.drawer}
        withinPortal={false}>
        {SelectedSubSection ? (
          <SelectedSubSection
            onGoBack={handleGoBack}
            closeDrawer={handleClose}
          />
        ) : (
          <div className={classes.menuRoot}>
            <header className={classes.closeHeader}>
              <ActionIcon
                className={classes.closeButton}
                size="md"
                onClick={handleClose}
                label="Close"
                name="feather/x"
              />
            </header>
            <ul className={classes.menu}>
              {Object.keys(SUB_SECTIONS).map((key) => {
                // Don't render menu items that require full login if the user
                // is not logged in, or logged in via Deep Link.
                if (
                  SUB_SECTIONS[key].requiresFullLogin &&
                  source !== AuthenticationSource.UserAccountAuthentication
                ) {
                  return null;
                }

                return (
                  <li key={key}>
                    <UnstyledButton
                      className={classes.listButton}
                      onClick={() => setSelectedSubSection(key)}>
                      {SUB_SECTIONS[key].label}
                      <Icon meaning="neutral" name="feather/chevron-right" />
                    </UnstyledButton>
                  </li>
                );
              })}
            </ul>

            <UnstyledButton
              className={classes.logoutButton}
              onClick={() => clearCacheAndLogout()}>
              <Icon name="feather/log-out" />
              Logout
            </UnstyledButton>
          </div>
        )}
      </Drawer>
      <Burger
        className={classes.burger}
        opened={opened}
        onClick={handleClose}
        size="sm"
        title="Menu"
        aria-label="User Menu"
      />
    </>
  );
};

export default PatientProfileDrawer;
